import { ListingLifeCycleState, PropertyListingDto, PropertyListingTypeDto } from 'src/app/services/property-matrixV2/models';
import { NotificationService } from 'src/app/shared/services/notification-service/notification.service';
import { PropertyListingService } from 'src/app/services/property-matrixV2/services';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { catchError, finalize, of, firstValueFrom } from 'rxjs';
import { AccountService } from 'src/app/services/auth/account-service';

@Component({
  selector: 'app-listing-card',
  templateUrl: './listing-card.component.html',
  styleUrls: ['./listing-card.component.scss']
})
export class ListingCardComponent implements OnInit {

  @Input() listing: PropertyListingDto;
  @Input() currentUserIndex: number = 0;
  @Input() users: Array<{ key: number, name: string, role: string, roleInitial: string, roleColor: string }> = [];

  @Output() listingClicked = new EventEmitter<PropertyListingDto>();
  @Output() listingClickedNewWindow = new EventEmitter<PropertyListingDto>();
  @Output() listingCardSelected = new EventEmitter<string>();
  @Output() listingUpdated = new EventEmitter<PropertyListingDto>();

  selected: boolean = false;
  addressTitle: string | null = null;
  completeAddress: string | null = null;
  surveyorGeneralKey: string | null = null;
  placeholderImageUrl: string = '../../../../../../assets/images/placeholders/property-matrix-placeholder.png';
  showManualReportIcon: boolean = false;
  showLevel1ReportIcon: boolean = false;
  showLevel2ReportIcon: boolean = false;
  showHomeIcon: boolean = false;
  propertyListingType: PropertyListingTypeDto;
  loading: boolean = false;
  selectedPropertyListing: PropertyListingDto;
  currentUserRoles: string[] = [];

  constructor(
    private notificationService: NotificationService,
    private propertyListingService: PropertyListingService,
    private accountService: AccountService,
  ) { }

  async ngOnInit() {
    this.addressTitle = this.listing.addressTitle === "Erf , " ? "Address Title Unavailable" : this.listing.addressTitle;
    this.completeAddress = this.listing.completeAddress || "No additional address details available";
    this.surveyorGeneralKey = this.listing.surveyorGeneralKey || "S.G. Code Unavailable";
    this.checkListingState();
    this.checkReportType();
    this.checkPropertyListingType();
    await this.loadCurrentUserRoles();
  }

  onError(event: Event) {
    const imgElement = event.target as HTMLImageElement;
    imgElement.src = this.placeholderImageUrl;
  }

  selectListingForMultiple() {
    this.selected = !this.selected;
    this.listingCardSelected.emit(this.listing.id);
  }

  archiveListing() {
    if (!(this.currentUserRoles?.some(ur => ur == "Network Agent" || ur == "Principal Agent") ?? false)) {

      this.listing.lifeCycleState = ListingLifeCycleState.Archived;
      this.propertyListingService.apiV1PropertyListingSetListingArchivedStatePost({ body: this.listing, })
        .pipe(
          catchError((error) => {
            this.notificationService.showErrorMessage('Error', 'Could not archive listing.');
            throw error;
          }),
          finalize(() => {
            this.notificationService.showSuccessMessage('Success', 'Listing archived successfully.');
          })
        ).subscribe(result => {
          this.listing = result;
          this.listingUpdated.emit(this.listing);
        });
    } else {
      this.notificationService.showWarningMessage('Warning', 'You dont have sufficient rights to archive this listing.');
    }
  }

  unarchiveListing() {
    if (!(this.currentUserRoles?.some(ur => ur == "Network Agent" || ur == "Principal Agent") ?? false)) {
      this.listing.lifeCycleState = ListingLifeCycleState.Assigned;
      this.propertyListingService.apiV1PropertyListingSetListingArchivedStatePost({ body: this.listing, })
        .pipe(
          catchError((error) => {
            this.notificationService.showErrorMessage('Error', 'Could not unarchive listing.');
            throw error;
          }),
          finalize(() => {
            this.notificationService.showSuccessMessage('Success', 'Listing unarchived successfully.');
          })
        ).subscribe(result => {
          this.listing = result;
          this.listingUpdated.emit(this.listing);
        });
    } else {
      this.notificationService.showWarningMessage('Warning', 'You dont have sufficient rights to unarchive this listing.');
    }
  }

  toggleSelectListing() {
    this.selected = !this.selected;
    this.listingCardSelected.emit(this.listing.id);
  }

  getArchiveButtonText() {
    return this.listing.lifeCycleState === ListingLifeCycleState.Archived ? 'Unarchive Listing' : 'Archive Listing';
  }

  getSelectButtonText() {
    return this.selected ? 'Deselect Listing' : 'Select Listing';
  }

  goToListingOverview(): void {
    this.listingClicked.emit(this.listing);
  }

  goToListingOverviewInNewWindow(): void {
    this.listingClickedNewWindow.emit(this.listing);
  }

  getCurrentUser(): any {
    let adjustedIndex = (this.currentUserIndex - 1) % this.users.length;
    return this.users[adjustedIndex];
  }

  deleteListing(): void {
    if (!(this.currentUserRoles?.some(ur => ur == "Network Agent" || ur == "Principal Agent") ?? false)) {
      this.notificationService.showConfirmMessage('Confirm Deletion', 'Are you sure you want to delete this listing?').then((confirmed) => {
        if (confirmed) {
          this.propertyListingService.apiV1PropertyListingDeletePropertyListingDelete({
            listingId: this.listing.id
          }).subscribe({
            next: () => {
              this.notificationService.showSuccessMessage('Success', 'Listing deleted successfully.');
              setTimeout(() => {
                window.location.reload();
              }, 1000);
            },
            error: (_error: any) => {
              this.notificationService.showErrorMessage('Error', 'Could not delete listing.');
            }
          });
        }
      });
    } else {
      this.notificationService.showWarningMessage('Warning', 'You dont have sufficient rights to delete this listing.');
    }
  }

  displayLifeCycleState: ListingLifeCycleState;

  checkListingState() {
    if (this.listing.isDeleted) {
      this.displayLifeCycleState = ListingLifeCycleState.Archived;
      return;
    }
    for (const task of this.listing.taskList) {
      if (task.taskStatus.taskStatusNumber == 2) {
        this.displayLifeCycleState = ListingLifeCycleState.Assigned;
        return;
      }
      if (task.taskNumber.number == 12 && task.taskStatus.taskStatusNumber == 3) {
        this.displayLifeCycleState = ListingLifeCycleState.Done;
        return;
      }
    }
    this.displayLifeCycleState = this.listing.lifeCycleState;
  }

  checkReportType(): void {
    this.showManualReportIcon = false;
    this.showLevel1ReportIcon = false;
    this.showLevel2ReportIcon = false;
    this.showHomeIcon = false;

    if (this.listing && this.listing.allocatePlannerReportSelection) {
      if (this.listing.allocatePlannerReportSelection === "Manual Report") {
        this.showManualReportIcon = true;
      } else if (this.listing.allocatePlannerReportSelection === "Level One Report") {
        this.showLevel1ReportIcon = true;
      } else if (this.listing.allocatePlannerReportSelection === "Level Two Report") {
        this.showLevel2ReportIcon = true;
      }
      else {
        this.showHomeIcon = true;
      }
    } else {
      this.showHomeIcon = true;
    }
  }
  private async checkPropertyListingType(): Promise<void> {
    if (this.listing && this.listing.propertyListingType) {
      this.propertyListingType = this.listing.propertyListingType;
    } else {
      let waitToFinish = await this.getPropertyListingTypeOfListingWithId();
      if (waitToFinish == false) {
        this.notificationService.showErrorMessage("Error", "Could not get the Property ListingData with Id.")
      }
    }
  }

  private async getPropertyListingTypeOfListingWithId(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      try {
        this.loading = true;
        this.propertyListingService.apiV1PropertyListingGetPropertyListingByIdGet({ listingId: this.listing.id })
          .pipe(
            catchError((err) =>
              of(this.notificationService.showErrorMessage("Error", "Could not get the Property ListingData with Id."))
            ),
            finalize(() => {
              this.loading = false;
            })
          )
          .subscribe((result: PropertyListingDto) => {
            this.selectedPropertyListing = result;
            if (this.selectedPropertyListing?.propertyListingType) {
              this.propertyListingType = this.selectedPropertyListing.propertyListingType;
            }
            resolve(true);
          });
      } catch (ex) {
        this.notificationService.showErrorMessage("Error", ex.toString());
        this.loading = false;
        resolve(false);
        reject("Could not get the Property ListingData with Id.");
      }
    });
  }

  private async loadCurrentUserRoles() {
    this.currentUserRoles = await this.accountService.getUserRoles();
  }

  isPrincipalOrAgent(): boolean {
    let returnValue = false;
    if (this.currentUserRoles?.some(ur => ur == "Network Agent" || ur == "Principal Agent") ?? false) {
      returnValue = true;
    }
    return returnValue;
  }
}
